import { EventDispatcher, Vector3, MOUSE, Quaternion, Spherical, Vector2 } from "three";
const STATE = {
  NONE: -1,
  ROTATE: 0,
  DOLLY: 1,
  PAN: 2,
  TOUCH_ROTATE: 3,
  TOUCH_DOLLY: 4,
  TOUCH_PAN: 5
};
const CHANGE_EVENT = {
  type: "change"
};
const START_EVENT = {
  type: "start"
};
const END_EVENT = {
  type: "end"
};
const EPS = 0.000001;
/**
 * @author qiao / https://github.com/qiao
 * @author mrdoob / http://mrdoob.com
 * @author alteredq / http://alteredqualia.com/
 * @author WestLangley / http://github.com/WestLangley
 * @author erich666 / http://erichaines.com
 * @author nicolaspanel / http://github.com/nicolaspanel
 *
 * This set of controls performs orbiting, dollying (zooming), and panning.
 * Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
 *    Orbit - left mouse / touch: one finger move
 *    Zoom - middle mouse, or mousewheel / touch: two finger spread or squish
 *    Pan - right mouse, or arrow keys / touch: three finger swipe
 */
export class OrbitControls extends EventDispatcher {
  constructor(object, domElement, domWindow, orbitOptions) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    super();
    Object.defineProperty(this, "camera", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "domElement", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "window", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    // API
    Object.defineProperty(this, "target", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "enableZoom", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "minDistance", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "maxDistance", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "enableRotate", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "rotateSpeed", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "enablePan", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "autoRotate", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "autoRotateSpeed", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "minZoom", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "maxZoom", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "scrollZoomSpeed", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "pinchZoomSpeed", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "minPolarAngle", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "maxPolarAngle", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "minAzimuthAngle", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "maxAzimuthAngle", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "mouseButtons", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "enableDamping", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "dampingFactor", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    // Ability to override rotations coming from user interactions
    Object.defineProperty(this, "overrideRotation", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "overrideTheta", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "overridePhi", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "spherical", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "sphericalDelta", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "scale", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "target0", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "position0", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "zoom0", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "state", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "panOffset", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "zoomChanged", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "rotateStart", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "rotateEnd", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "rotateDelta", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "panStart", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "panEnd", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "panDelta", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "dollyStart", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "dollyEnd", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "dollyDelta", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "updateLastPosition", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "updateOffset", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "updateQuat", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "updateLastQuaternion", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "updateQuatInverse", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "panLeftV", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "panUpV", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "panInternalOffset", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "minPanBounds", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "maxPanBounds", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "onContextMenu", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "onMouseUp", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "onMouseDown", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "onMouseMove", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "onMouseWheel", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "onTouchStart", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "onTouchEnd", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "onTouchMove", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "mouseGestures", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {
        leftClickDrag: "rotate",
        rightClickDrag: "pan",
        scroll: "zoom"
      }
    });
    Object.defineProperty(this, "touchGestures", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: {
        singleFingerDrag: "rotate",
        twoFingerPinch: "zoom",
        threeFingerDrag: "pan"
      }
    });
    this.camera = object;
    this.domElement = domElement !== undefined ? domElement : document;
    this.window = domWindow !== undefined ? domWindow : window;
    // "target" sets the location of focus, where the object orbits around
    this.target = new Vector3();
    // How far you can dolly in and out ( PerspectiveCamera only )
    this.minDistance = 0;
    this.maxDistance = Infinity;
    // How far you can zoom in and out ( OrthographicCamera only )
    this.minZoom = 0;
    this.maxZoom = Infinity;
    // How far you can orbit vertically, upper and lower limits.
    // Range is 0 to Math.PI radians.
    this.minPolarAngle = (_b = (_a = orbitOptions === null || orbitOptions === void 0 ? void 0 : orbitOptions.polarAngle) === null || _a === void 0 ? void 0 : _a.min) !== null && _b !== void 0 ? _b : 0; // radians
    this.maxPolarAngle = (_d = (_c = orbitOptions === null || orbitOptions === void 0 ? void 0 : orbitOptions.polarAngle) === null || _c === void 0 ? void 0 : _c.max) !== null && _d !== void 0 ? _d : Math.PI; // radians
    // How far you can orbit horizontally, upper and lower limits.
    // If set, must be a sub-interval of the interval [ - Math.PI, Math.PI ].
    this.minAzimuthAngle = (_f = (_e = orbitOptions === null || orbitOptions === void 0 ? void 0 : orbitOptions.azimuthAngle) === null || _e === void 0 ? void 0 : _e.min) !== null && _f !== void 0 ? _f : -Infinity; // radians
    this.maxAzimuthAngle = (_h = (_g = orbitOptions === null || orbitOptions === void 0 ? void 0 : orbitOptions.azimuthAngle) === null || _g === void 0 ? void 0 : _g.max) !== null && _h !== void 0 ? _h : Infinity; // radians
    // Set to true to enable damping (inertia)
    // If damping is enabled, you must call controls.update() in your animation loop
    this.enableDamping = false;
    this.dampingFactor = 0.25;
    // This option actually enables dollying in and out; left as "zoom" for backwards compatibility.
    // Set to false to disable zooming
    this.enableZoom = true;
    this.scrollZoomSpeed = 1.0;
    this.pinchZoomSpeed = 1.0;
    // Set to false to disable rotating
    this.enableRotate = true;
    this.rotateSpeed = 1.0;
    // Set to false to disable panning
    this.enablePan = true;
    // Set to true to automatically rotate around the target
    // If auto-rotate is enabled, you must call controls.update() in your animation loop
    this.autoRotate = false;
    this.autoRotateSpeed = 2.0; // 30 seconds per round when fps is 60
    // Mouse buttons
    this.mouseButtons = {
      ORBIT: MOUSE.LEFT,
      ZOOM: MOUSE.MIDDLE,
      PAN: MOUSE.RIGHT
    };
    // for reset
    this.target0 = this.target.clone();
    this.position0 = this.camera.position.clone();
    this.zoom0 = this.camera.zoom;
    // for update speedup
    this.updateOffset = new Vector3();
    // so camera.up is the orbit axis
    this.updateQuat = new Quaternion().setFromUnitVectors(object.up, new Vector3(0, 1, 0));
    this.updateQuatInverse = this.updateQuat.clone().inverse();
    this.updateLastPosition = new Vector3();
    this.updateLastQuaternion = new Quaternion();
    this.state = STATE.NONE;
    this.scale = 1;
    // current position in spherical coordinates
    this.spherical = new Spherical();
    this.sphericalDelta = new Spherical();
    this.panOffset = new Vector3();
    this.zoomChanged = false;
    this.rotateStart = new Vector2();
    this.rotateEnd = new Vector2();
    this.rotateDelta = new Vector2();
    this.panStart = new Vector2();
    this.panEnd = new Vector2();
    this.panDelta = new Vector2();
    this.dollyStart = new Vector2();
    this.dollyEnd = new Vector2();
    this.dollyDelta = new Vector2();
    this.panLeftV = new Vector3();
    this.panUpV = new Vector3();
    this.panInternalOffset = new Vector3();
    this.overrideRotation = false;
    this.overrideTheta = 0;
    this.overridePhi = 0;
    // event handlers - FSM: listen for events and reset state
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.onMouseDown = event => {
      event.preventDefault();
      if (event.button === this.mouseButtons.ORBIT) {
        if (this.mouseGestures.leftClickDrag === "rotate" && this.enableRotate) {
          this.rotateStart.set(event.clientX, event.clientY);
          this.state = STATE.ROTATE;
        }
        if (this.mouseGestures.leftClickDrag === "pan" && this.enablePan) {
          this.panStart.set(event.clientX, event.clientY);
          this.state = STATE.PAN;
        }
      } else if (event.button === this.mouseButtons.ZOOM) {
        if (this.enableZoom === false) return;
        this.dollyStart.set(event.clientX, event.clientY);
        this.state = STATE.DOLLY;
      } else if (event.button === this.mouseButtons.PAN) {
        if (this.enablePan === false) return;
        this.panStart.set(event.clientX, event.clientY);
        this.state = STATE.PAN;
      }
      if (this.state !== STATE.NONE) {
        document.addEventListener("mousemove", this.onMouseMove, false);
        document.addEventListener("mouseup", this.onMouseUp, false);
        this.dispatchEvent(START_EVENT);
      }
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.onMouseMove = event => {
      event.preventDefault();
      if (this.state === STATE.ROTATE) {
        if (this.mouseGestures.leftClickDrag === "rotate" && this.enableRotate) {
          this.rotateEnd.set(event.clientX, event.clientY);
          this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart);
          const element = this.domElement === document ? this.domElement.body : this.domElement;
          // rotating across whole screen goes 360 degrees around
          this.rotateLeft(2 * Math.PI * this.rotateDelta.x /
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          element.clientWidth * this.rotateSpeed);
          // rotating up and down along whole screen attempts to go 360, but limited to 180
          this.rotateUp(2 * Math.PI * this.rotateDelta.y /
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          element.clientHeight * this.rotateSpeed);
          this.rotateStart.copy(this.rotateEnd);
        }
        if (this.mouseGestures.leftClickDrag === "pan" && this.enablePan) {
          this.panEnd.set(event.clientX, event.clientY);
          this.panDelta.subVectors(this.panEnd, this.panStart);
          this.pan(this.panDelta.x, this.panDelta.y);
          this.panStart.copy(this.panEnd);
        }
        this.update();
      } else if (this.state === STATE.DOLLY) {
        if (this.enableZoom === false) return;
        this.dollyEnd.set(event.clientX, event.clientY);
        this.dollyDelta.subVectors(this.dollyEnd, this.dollyStart);
        if (this.dollyDelta.y > 0) {
          this.dollyIn(Math.pow(0.95, this.scrollZoomSpeed));
        } else if (this.dollyDelta.y < 0) {
          this.dollyOut(Math.pow(0.95, this.scrollZoomSpeed));
        }
        this.dollyStart.copy(this.dollyEnd);
        this.update();
      } else if (this.state === STATE.PAN) {
        if (this.enablePan === false) return;
        this.panEnd.set(event.clientX, event.clientY);
        this.panDelta.subVectors(this.panEnd, this.panStart);
        this.pan(this.panDelta.x, this.panDelta.y);
        this.panStart.copy(this.panEnd);
        this.update();
      }
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.onMouseUp = () => {
      document.removeEventListener("mousemove", this.onMouseMove, false);
      document.removeEventListener("mouseup", this.onMouseUp, false);
      this.dispatchEvent(END_EVENT);
      this.state = STATE.NONE;
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.onMouseWheel = event => {
      if (this.enableZoom === false || this.state !== STATE.NONE && this.state !== STATE.ROTATE) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.deltaY < 0) {
        this.dollyOut(Math.pow(0.95, this.scrollZoomSpeed));
      } else if (event.deltaY > 0) {
        this.dollyIn(Math.pow(0.95, this.scrollZoomSpeed));
      }
      this.update();
      this.dispatchEvent(START_EVENT); // not sure why these are here...
      this.dispatchEvent(END_EVENT);
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.onTouchStart = event => {
      switch (event.touches.length) {
        // one-fingered touch
        case 1:
          if (this.touchGestures.singleFingerDrag === "rotate" && this.enableRotate) {
            this.rotateStart.set(event.touches[0].pageX, event.touches[0].pageY);
            this.state = STATE.TOUCH_ROTATE;
          }
          if (this.touchGestures.singleFingerDrag === "pan" && this.enablePan) {
            this.panStart.set(event.touches[0].pageX, event.touches[0].pageY);
            this.state = STATE.TOUCH_PAN;
          }
          break;
        // two-fingered touch: dolly
        case 2:
          {
            const dx = event.touches[0].pageX - event.touches[1].pageX;
            const dy = event.touches[0].pageY - event.touches[1].pageY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (this.enableZoom === false) return;
            this.dollyStart.set(0, distance);
            this.state = STATE.TOUCH_DOLLY;
            break;
          }
        // three-fingered touch: pan
        case 3:
          if (this.enablePan === false) return;
          this.panStart.set(event.touches[0].pageX, event.touches[0].pageY);
          this.state = STATE.TOUCH_PAN;
          break;
        default:
          this.state = STATE.NONE;
      }
      if (this.state !== STATE.NONE) {
        this.dispatchEvent(START_EVENT);
      }
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.onTouchMove = event => {
      event.preventDefault();
      event.stopPropagation();
      const element = this.domElement === document ? this.domElement.body : this.domElement;
      switch (event.touches.length) {
        // one-fingered touch
        case 1:
          if (this.touchGestures.singleFingerDrag === "rotate" && this.state === STATE.TOUCH_ROTATE && this.enableRotate) {
            this.rotateEnd.set(event.touches[0].pageX, event.touches[0].pageY);
            this.rotateDelta.subVectors(this.rotateEnd, this.rotateStart);
            // rotating across whole screen goes 360 degrees around
            this.rotateLeft(2 * Math.PI * this.rotateDelta.x /
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            element.clientWidth * this.rotateSpeed);
            // rotating up and down along whole screen attempts to go 360, but limited to 180
            this.rotateUp(2 * Math.PI * this.rotateDelta.y /
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            element.clientHeight * this.rotateSpeed);
            this.rotateStart.copy(this.rotateEnd);
            this.update();
          }
          if (this.touchGestures.singleFingerDrag === "pan" && this.state === STATE.TOUCH_PAN && this.enablePan) {
            this.panEnd.set(event.touches[0].pageX, event.touches[0].pageY);
            this.panDelta.subVectors(this.panEnd, this.panStart);
            this.pan(this.panDelta.x, this.panDelta.y);
            this.panStart.copy(this.panEnd);
            this.update();
          }
          break;
        // two-fingered touch: dolly
        case 2:
          {
            const dx = event.touches[0].pageX - event.touches[1].pageX;
            const dy = event.touches[0].pageY - event.touches[1].pageY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (this.enableZoom === false || this.state !== STATE.TOUCH_DOLLY) return; // is this needed?...
            this.dollyEnd.set(0, distance);
            this.dollyDelta.subVectors(this.dollyEnd, this.dollyStart);
            if (this.dollyDelta.y > 0) {
              this.dollyOut(Math.pow(0.95, this.pinchZoomSpeed));
            } else if (this.dollyDelta.y < 0) {
              this.dollyIn(Math.pow(0.95, this.pinchZoomSpeed));
            }
            this.dollyStart.copy(this.dollyEnd);
            this.update();
            break;
          }
        // three-fingered touch: pan
        case 3:
          if (this.enablePan === false) return;
          if (this.state !== STATE.TOUCH_PAN) return; // is this needed?...
          this.panEnd.set(event.touches[0].pageX, event.touches[0].pageY);
          this.panDelta.subVectors(this.panEnd, this.panStart);
          this.pan(this.panDelta.x, this.panDelta.y);
          this.panStart.copy(this.panEnd);
          this.update();
          break;
        default:
          this.state = STATE.NONE;
      }
    };
    this.onTouchEnd = () => {
      this.dispatchEvent(END_EVENT);
      this.state = STATE.NONE;
    };
    this.onContextMenu = event => {
      event.preventDefault();
    };
    this.domElement.addEventListener("contextmenu", this.onContextMenu, false);
    this.domElement.addEventListener("mousedown", this.onMouseDown, false);
    this.domElement.addEventListener("wheel", this.onMouseWheel, false);
    this.domElement.addEventListener("touchstart", this.onTouchStart, false);
    this.domElement.addEventListener("touchend", this.onTouchEnd, false);
    this.domElement.addEventListener("touchmove", this.onTouchMove, false);
    // force an update at start
    this.update();
  }
  update() {
    const {
      position
    } = this.camera;
    this.updateOffset.copy(position).sub(this.target);
    // rotate offset to "y-axis-is-up" space
    this.updateOffset.applyQuaternion(this.updateQuat);
    // Use rotation overrides, or deltas from user interactions
    if (this.overrideRotation) {
      this.spherical.theta = this.overrideTheta;
      this.spherical.phi = this.overridePhi;
      this.overrideRotation = false;
    } else {
      this.spherical.theta += this.sphericalDelta.theta;
      this.spherical.phi += this.sphericalDelta.phi;
    }
    // restrict theta to be between desired limits
    this.spherical.theta = Math.max(this.minAzimuthAngle, Math.min(this.maxAzimuthAngle, this.spherical.theta));
    // restrict phi to be between desired limits
    this.spherical.phi = Math.max(this.minPolarAngle, Math.min(this.maxPolarAngle, this.spherical.phi));
    this.spherical.makeSafe();
    this.spherical.radius *= this.scale;
    // restrict radius to be between desired limits
    this.spherical.radius = Math.max(this.minDistance, Math.min(this.maxDistance, this.spherical.radius));
    // move target to panned location
    this.target.add(this.panOffset);
    // restrict target to vortex bounds
    if (this.minPanBounds && this.maxPanBounds) {
      this.target.x = Math.max(this.minPanBounds.x, Math.min(this.maxPanBounds.x, this.target.x));
      this.target.y = Math.max(this.minPanBounds.y, Math.min(this.maxPanBounds.y, this.target.y));
      this.target.z = Math.max(this.minPanBounds.z, Math.min(this.maxPanBounds.z, this.target.z));
    }
    this.updateOffset.setFromSpherical(this.spherical);
    // rotate offset back to "camera-up-vector-is-up" space
    this.updateOffset.applyQuaternion(this.updateQuatInverse);
    position.copy(this.target).add(this.updateOffset);
    this.camera.lookAt(this.target);
    if (this.enableDamping === true) {
      this.sphericalDelta.theta *= 1 - this.dampingFactor;
      this.sphericalDelta.phi *= 1 - this.dampingFactor;
    } else {
      this.sphericalDelta.set(0, 0, 0);
    }
    this.scale = 1;
    this.panOffset.set(0, 0, 0);
    // update condition is:
    // min(camera displacement, camera rotation in radians)^2 > EPS
    // using small-angle approximation cos(x/2) = 1 - x^2 / 8
    if (this.zoomChanged || this.updateLastPosition.distanceToSquared(this.camera.position) > EPS || 8 * (1 - this.updateLastQuaternion.dot(this.camera.quaternion)) > EPS) {
      this.dispatchEvent(CHANGE_EVENT);
      this.updateLastPosition.copy(this.camera.position);
      this.updateLastQuaternion.copy(this.camera.quaternion);
      this.zoomChanged = false;
      return true;
    }
    return false;
  }
  panLeft(distance, objectMatrix) {
    this.panLeftV.setFromMatrixColumn(objectMatrix, 0); // get X column of objectMatrix
    this.panLeftV.multiplyScalar(-distance);
    this.panOffset.add(this.panLeftV);
  }
  panUp(distance, objectMatrix) {
    this.panUpV.setFromMatrixColumn(objectMatrix, 1); // get Y column of objectMatrix
    this.panUpV.multiplyScalar(distance);
    this.panOffset.add(this.panUpV);
  }
  // deltaX and deltaY are in pixels; right and down are positive
  pan(deltaX, deltaY) {
    const element = this.domElement === document ? this.domElement.body : this.domElement;
    const {
      position
    } = this.camera;
    this.panInternalOffset.copy(position).sub(this.target);
    let targetDistance = this.panInternalOffset.length();
    // half of the fov is center to top of screen
    targetDistance *= Math.tan(this.camera.fov / 2 * Math.PI / 180.0);
    // we actually don't use screenWidth, since perspective camera is fixed to screen height
    this.panLeft(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    2 * deltaX * targetDistance / element.clientHeight, this.camera.matrix);
    this.panUp(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    2 * deltaY * targetDistance / element.clientHeight, this.camera.matrix);
  }
  dollyIn(dollyScale) {
    this.scale /= dollyScale;
  }
  dollyOut(dollyScale) {
    this.scale *= dollyScale;
  }
  getAutoRotationAngle() {
    return 2 * Math.PI / 60 / (60 * this.autoRotateSpeed);
  }
  setTarget(point) {
    this.target.copy(point);
  }
  setRadius(radius) {
    // Directly set radius and reset scale factor
    this.spherical.radius = radius;
    this.scale = 1.0;
  }
  setRadiusLimits(min, max) {
    // Update scaling limits
    this.minDistance = min;
    this.maxDistance = max;
  }
  setPanBounds(min, max) {
    this.minPanBounds = new Vector3(min.x, min.y, min.z);
    this.maxPanBounds = new Vector3(max.x, max.y, max.z);
  }
  // Overrides the existing rotations coming from user interactions
  setRotation(azimuth, polar) {
    this.overrideRotation = true;
    this.overrideTheta = azimuth;
    this.overridePhi = polar;
  }
  rotateLeft(angle) {
    this.sphericalDelta.theta -= angle;
  }
  rotateUp(angle) {
    this.sphericalDelta.phi -= angle;
  }
  getPolarAngle() {
    if (this.overrideRotation) {
      return this.overridePhi;
    }
    return this.spherical.phi;
  }
  getAzimuthalAngle() {
    if (this.overrideRotation) {
      return this.overrideTheta;
    }
    return this.spherical.theta;
  }
  getDirection() {
    const direction = new Vector3().setFromSpherical(this.spherical).normalize();
    return new Vector3(-direction.x, -direction.y, -direction.z);
  }
  getOrbitRadius() {
    return this.spherical.radius;
  }
  dispose() {
    this.domElement.removeEventListener("contextmenu", this.onContextMenu, false);
    this.domElement.removeEventListener("mousedown", this.onMouseDown, false);
    this.domElement.removeEventListener("wheel", this.onMouseWheel, false);
    this.domElement.removeEventListener("touchstart", this.onTouchStart, false);
    this.domElement.removeEventListener("touchend", this.onTouchEnd, false);
    this.domElement.removeEventListener("touchmove", this.onTouchMove, false);
    document.removeEventListener("mousemove", this.onMouseMove, false);
    document.removeEventListener("mouseup", this.onMouseUp, false);
    // this.dispatchEvent( { type: 'dispose' } ); // should this be added here?
  }
  reset() {
    this.target.copy(this.target0);
    this.camera.position.copy(this.position0);
    this.camera.zoom = this.zoom0;
    this.camera.updateProjectionMatrix();
    this.dispatchEvent(CHANGE_EVENT);
    this.update();
    this.state = STATE.NONE;
  }
  saveState() {
    this.target0.copy(this.target);
    this.position0.copy(this.camera.position);
    this.zoom0 = this.camera.zoom;
  }
}